import { ProductKind, ProductSegment, ProductType } from 'constants/product'

import { CatalogueProduct } from 'dataQueries/purchase.query'
import { ProductCategory } from 'models/product'
import { editingProductKindSortingOrder } from 'constants/purchaseFlow'
import { isEditingCategory } from 'utils/validators'

/** A product type together with every catalogue product offered under it. */
export type ProductTypeWithProducts = {
  productType: ProductType
  products: Array<CatalogueProduct>
}

/** A product kind together with every catalogue product offered under it. */
export type ProductKindWithProducts = {
  productKind: ProductKind
  products: Array<CatalogueProduct>
}

/** Product types available per segment, split into bundles and single products. */
export type ProductTypesPerSegmentCollection = Partial<Record<ProductSegment, {
  bundle: Array<ProductTypeWithProducts>,
  nonBundle: Array<ProductTypeWithProducts>,
  all: Array<ProductTypeWithProducts>,
}>>

/** Product kinds available per segment, used by the editing listing. */
export type ProductKindsPerSegmentCollection = Partial<Record<ProductSegment, Array<ProductKindWithProducts>>>

/**
 * Whether a category ignores the catalogue's segmentation. An editing order never collects a
 * property surface, so the client has no basis for picking an area band: the slider stays hidden
 * and every product is listed whatever band the catalogue put it in.
 */
export const isSegmentationExemptCategory = (category: ProductCategory) => isEditingCategory(category)

const byAscendingPrice = (productA: CatalogueProduct, productB: CatalogueProduct) => productA.price - productB.price

/**
 * Concatenates products of one type/kind across segments, dropping any repeated by id. A product
 * carries a SET of segments, and the catalogue lists the same product object under each of them, so
 * a multi-band product would otherwise render one identical card per band.
 */
const mergeProducts = (groups: Array<Array<CatalogueProduct>>) => {
  const merged: Array<CatalogueProduct> = []
  const seenIds = new Set<number>()

  for (const products of groups) {
    for (const product of products) {
      if (seenIds.has(product.id)) continue
      seenIds.add(product.id)
      merged.push(product)
    }
  }

  return merged.sort(byAscendingPrice)
}

const mergeTypes = (entriesPerSegment: Array<Array<ProductTypeWithProducts>>) => {
  const productsByType = new Map<ProductType, Array<Array<CatalogueProduct>>>()

  for (const entries of entriesPerSegment) {
    for (const { productType, products } of entries) {
      productsByType.set(productType, [...(productsByType.get(productType) || []), products])
    }
  }

  return Array.from(productsByType, ([productType, groups]) => ({ productType, products: mergeProducts(groups) }))
    .sort((typeA, typeB) => typeA.productType.localeCompare(typeB.productType))
}

const mergeKinds = (entriesPerSegment: Array<Array<ProductKindWithProducts>>) => {
  const productsByKind = new Map<ProductKind, Array<Array<CatalogueProduct>>>()

  for (const entries of entriesPerSegment) {
    for (const { productKind, products } of entries) {
      productsByKind.set(productKind, [...(productsByKind.get(productKind) || []), products])
    }
  }

  return Array.from(productsByKind, ([productKind, groups]) => ({ productKind, products: mergeProducts(groups) }))
    .sort((kindGroupA, kindGroupB) => editingProductKindSortingOrder.indexOf(kindGroupA.productKind) - editingProductKindSortingOrder.indexOf(kindGroupB.productKind))
}

/**
 * Collapses every segment of a computed catalogue into a single `all` bucket, so a segmentation-exempt
 * category offers all of its products at once instead of only the selected band's. An empty catalogue
 * stays empty: a fabricated `all` segment would claim the country has products when it has none.
 */
export function collapseSegmentsToAll(
  typesPerSegment: ProductTypesPerSegmentCollection,
  kindsPerSegment: ProductKindsPerSegmentCollection
): {
  availableSegments: Array<ProductSegment>
  availableProductTypesPerSegment: ProductTypesPerSegmentCollection
  availableProductKindsPerSegment: ProductKindsPerSegmentCollection
} {
  const typeSegments = Object.values(typesPerSegment).filter((segment) => !!segment)
  const kindSegments = Object.values(kindsPerSegment).filter((segment) => !!segment)

  if (!typeSegments.length && !kindSegments.length) return {
    availableSegments: [],
    availableProductTypesPerSegment: {},
    availableProductKindsPerSegment: {},
  }

  return {
    availableSegments: [ProductSegment.all],
    availableProductTypesPerSegment: {
      [ProductSegment.all]: {
        bundle: mergeTypes(typeSegments.map(({ bundle }) => bundle)),
        nonBundle: mergeTypes(typeSegments.map(({ nonBundle }) => nonBundle)),
        all: mergeTypes(typeSegments.map(({ all }) => all)),
      },
    },
    availableProductKindsPerSegment: {
      [ProductSegment.all]: mergeKinds(kindSegments),
    },
  }
}
